import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const materialSchema = z.object({
  name: z.string().min(1, "Nimi on pakollinen"),
  sku: z.string().optional(),
  category: z.string().optional(),
  unit: z.string().min(1, "Yksikkö on pakollinen"),
  unit_cost: z.coerce.number().min(0, "Yksikköhinnan on oltava positiivinen"),
  description: z.string().optional(),
  supplier: z.string().optional(),
  is_active: z.boolean().default(true),
});

type MaterialFormValues = z.infer<typeof materialSchema>;

interface MaterialData {
  id: string;
  name: string;
  sku: string | null;
  category: string | null;
  unit: string;
  unit_cost: number | null;
  description: string | null;
  supplier: string | null;
  is_active: boolean | null;
}

interface MaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  material?: MaterialData | null;
}

export function MaterialDialog({ open, onOpenChange, material }: MaterialDialogProps) {
  const queryClient = useQueryClient();
  const isEditing = !!material;

  const form = useForm<MaterialFormValues>({
    resolver: zodResolver(materialSchema),
    defaultValues: {
      name: "",
      sku: "",
      category: "",
      unit: "m²",
      unit_cost: 0,
      description: "",
      supplier: "",
      is_active: true,
    },
  });

  useEffect(() => {
    if (material) {
      form.reset({
        name: material.name || "",
        sku: material.sku || "",
        category: material.category || "",
        unit: material.unit || "m²",
        unit_cost: material.unit_cost || 0,
        description: material.description || "",
        supplier: material.supplier || "",
        is_active: material.is_active ?? true,
      });
    } else {
      form.reset({
        name: "",
        sku: "",
        category: "",
        unit: "m²",
        unit_cost: 0,
        description: "",
        supplier: "",
        is_active: true,
      });
    }
  }, [material, form]);

  const createMutation = useMutation({
    mutationFn: async (values: MaterialFormValues) => {
      const { error } = await supabase.from("materials").insert([{
        name: values.name,
        sku: values.sku || null,
        category: values.category || null,
        unit: values.unit,
        unit_cost: values.unit_cost,
        description: values.description || null,
        supplier: values.supplier || null,
        is_active: values.is_active,
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      toast.success("Materiaali luotu");
      onOpenChange(false);
      form.reset();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (values: MaterialFormValues) => {
      const { error } = await supabase
        .from("materials")
        .update({
          name: values.name,
          sku: values.sku || null,
          category: values.category || null,
          unit: values.unit,
          unit_cost: values.unit_cost,
          description: values.description || null,
          supplier: values.supplier || null,
          is_active: values.is_active,
        })
        .eq("id", material!.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      toast.success("Materiaali päivitetty");
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const onSubmit = (values: MaterialFormValues) => {
    if (isEditing) {
      updateMutation.mutate(values);
    } else {
      createMutation.mutate(values);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Muokkaa materiaalia" : "Lisää materiaali"}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Nimi *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="esim. Musta graniitti" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sku"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SKU</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="MAT-001" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Kategoria</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Valitse kategoria" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Kivet">Kivet</SelectItem>
                        <SelectItem value="Pohjat">Pohjat</SelectItem>
                        <SelectItem value="Kirjoitukset">Kirjoitukset</SelectItem>
                        <SelectItem value="Työaika">Työaika</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
              )}
            />
          </div>

            <div className="grid grid-cols-2 gap-4 border-t pt-4">
              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Yksikkö *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Valitse yksikkö" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="m²">m² (neliömetri)</SelectItem>
                        <SelectItem value="m³">m³ (kuutiometri)</SelectItem>
                        <SelectItem value="m">m (metri)</SelectItem>
                        <SelectItem value="h">h (tunti)</SelectItem>
                        <SelectItem value="kpl">kpl (kappale)</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit_cost"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Yksikköhinta (€)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Kuvaus</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="supplier"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Toimittaja</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Toimittajan nimi" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Aktiivinen</FormLabel>
                    <FormDescription>
                      Aktiiviset materiaalit näkyvät tuotevalinnoissa
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Peruuta
              </Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {isEditing ? "Tallenna muutokset" : "Luo materiaali"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
